/**
 * PaymentChannelReadinessPanel —— 支付通道下发的**负向清单**（阻断项）。
 *
 * 挂在 App 详情「支付渠道」tab，回答运营的一个问题：**什么在阻止这个通道被 C 端下发**。
 *
 * **绿灯位根本不画。** 判据全不命中时显示的是「未发现阻断项」+ 一条逐项列全的信息边界声明，
 * 而不是「会被下发 ✅」—— C 端那条合取式之后还有若干本页核不到的收窄（回跳 host / SRV-PAY-003 /
 * SRV-PAY-033 / new_user_offer 7 天窗 / 发现面归属排除），逐通道列在 `blindSpots`。
 *
 * **四态而非三态**：除「阻断 / 无阻断 / 无法判定」外还有**不适用** —— 只卖 IAP 的 App 里 STRIPE
 * 不是「被阻断」，是运营在上方策略组面板关掉的。判据、维度与依据见
 * `~/data/adapters/payment-channel-readiness-adapter`。
 *
 * 数据全部复用既有 hook，**不新增后端端点**（A8 §7.5.1 已确立「无独立 endpoint、纯前端聚合」体例）：
 * `usePaymentMethodPolicies` / `useAppPaymentMethods` / `useStripeCheckoutConfigs` / `useProductMetadata`，
 * 框架通道另读 `usePaymentProviderConfigs`（A8 §10.1，viewer+；与配置页同一 query key，配置页写入后即刷新）
 * 判配置就绪 / 原生回跳 / 回调地址。
 *
 * **fail-closed**：四个数据源统一走 `readinessSourceState`（**只有 `isSuccess` 才算 ready**），
 * 任一未就绪 → 对应项显示「无法判定」，绝不渲染成「没有阻断项」。判据落在 adapter，本组件只做
 * hook flag → `ReadinessSourceState` 的映射与呈现。
 */

import {
  FRAMEWORK_SETTLEMENT_CHANNELS,
  type FrameworkSettlementChannel,
  type PaymentProviderConfigResponse,
  type SettlementChannel,
  isFrameworkSettlementChannel,
} from '@novel-admin/shared';
import { useNavigate } from 'react-router-dom';
import { useAppScope } from '~/app/app-scope-store';
import {
  type PaymentChannelReadiness,
  READINESS_CHANNEL_LABEL,
  type ReadinessActionTarget,
  type ReadinessCheck,
  type ReadinessItem,
  buildPaymentChannelReadiness,
  readinessSourceState,
} from '~/data/adapters/payment-channel-readiness-adapter';
import { usePaymentProviderConfigs } from '~/data/payment-provider-config-queries';
import {
  useAppPaymentMethods,
  usePaymentMethodOptions,
  usePaymentMethodPolicies,
  useProductMetadata,
  useStripeCheckoutConfigs,
} from '~/data/queries';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { FormRow } from '~/ui/FormRow';
import { Section } from '~/ui/Section';

/**
 * 三方通道在前：本面板的立项动因是「什么在挡三方通道」，IAP 同口径顺带列出。
 * 框架通道按 shared 已登记闭集生成（登记新通道即出现，不在页面里写死）。
 */
const READINESS_CHANNEL_ORDER: SettlementChannel[] = [
  'STRIPE',
  ...FRAMEWORK_SETTLEMENT_CHANNELS,
  'IAP',
];

/**
 * 每条阻断 / 中性项的处置入口。
 *
 * `to === null` = 就在本页（同 tab 的上方面板，或本页另一个 tab）—— 只给文字指路，不做路由跳转。
 * `requiresRootAdmin` 的入口对 operator 渲染成「需 root / admin 前往 …」，不给点了必被拒的按钮
 * （admin-api 的角色拒绝是 422 + `FORBIDDEN_OPERATION`，HTTP 403 在本仓不可能出现）。
 * `/paywall` 跳转沿用同 Section 上方 `DetailPayment` 的既有体例（setAppId + navigate）。
 */
interface ReadinessActionSpec {
  /** 处置位置的人话说法（无跳转入口时直接指路，故不含「去」字）*/
  where: string;
  /** 跳转按钮文案；`to === null` 时不用 */
  buttonLabel: string | null;
  /** 路由目标；null = 本页内（另一个 tab / 上方面板），不做路由跳转 */
  to: string | null;
  requiresRootAdmin: boolean;
}

const READINESS_ACTIONS: Record<ReadinessActionTarget, ReadinessActionSpec> = {
  app_danger_tab: {
    where: '本页「危险操作」tab',
    buttonLabel: null,
    to: null,
    // App 启停 = root/admin（与 server requireRole 及本页 useCanManageApp 同口径）
    requiresRootAdmin: true,
  },
  policy_panel: {
    where: '上方「通道策略」面板',
    buttonLabel: null,
    to: null,
    requiresRootAdmin: false,
  },
  paywall_pages: {
    where: '支付管理 › 支付页面',
    buttonLabel: '去支付页面配置',
    to: '/paywall?tab=pages',
    requiresRootAdmin: false,
  },
  paywall_popup_page: {
    where: '支付管理 › 支付页面 › 运营弹窗',
    buttonLabel: '去支付页配置弹窗落点',
    to: '/paywall?tab=pages&scenario=operational_popup',
    requiresRootAdmin: false,
  },
  paywall_products: {
    where: '支付管理 › 支付产品',
    buttonLabel: '去支付产品配置',
    to: '/paywall?tab=products',
    requiresRootAdmin: false,
  },
  stripe_integration: {
    where: '设置 › 集成 › Stripe Checkout',
    buttonLabel: '去 Stripe 集成配置',
    to: '/settings/integrations',
    // 端点本身是 requireRole('root','admin')，给 operator 一个点了必被拒（422 +
    // FORBIDDEN_OPERATION）的按钮只会误导
    requiresRootAdmin: true,
  },
  // 框架通道配置页按通道分路由；where / 按钮 / 路径由 `providerConfigAction` 按通道填入
  provider_config: {
    where: '设置 › 集成',
    buttonLabel: null,
    to: null,
    // 配置与回调登记的写端点是 requireRole('root','admin')：operator 只给「需谁处理」文案
    requiresRootAdmin: true,
  },
};

/** 框架通道配置入口（设置 › 集成 › {通道}，路由 `/settings/integrations/payment-providers/:channel`）*/
function providerConfigAction(channel: SettlementChannel): ReadinessActionSpec {
  const label = READINESS_CHANNEL_LABEL[channel];
  return {
    where: `设置 › 集成 › ${label}`,
    buttonLabel: `去 ${label} 配置`,
    to: `/settings/integrations/payment-providers/${channel}`,
    requiresRootAdmin: true,
  };
}

export function PaymentChannelReadinessPanel({
  appId,
  isActive,
  isNativeApp = true,
}: {
  appId: string;
  /** `apps.is_active` —— C 端 findAppById 的 where 含它，停用即全通道不下发（最高优先级前置）*/
  isActive: boolean;
  /**
   * 原生 App（非 WEB）才核框架通道的原生回跳。本面板目前只挂在原生 App 详情页（AppsPage 对 WEB 不渲染），
   * 故缺省 true。
   */
  isNativeApp?: boolean;
}) {
  const policies = usePaymentMethodPolicies(appId);
  // 成员 → 结算通道取 §7.8.8 可选列表的 `channel`（目录行属性），与策略组一起才能判 ①
  const paymentMethodOptions = usePaymentMethodOptions(appId);
  const paymentMethods = useAppPaymentMethods(appId);
  // 该 hook 返全量 App 列表（无 per-app 入参）；按 app_id 过滤在 adapter 内做。
  // 端点是 requireRole('root','admin')，operator 恒 422 + FORBIDDEN_OPERATION（admin-api 的角色
  // 拒绝走 errors.forbidden()，httpStatus 写死 422；HTTP 403 在本仓不可能出现）—— 靠 isForbidden
  // 与故障区分开。
  const stripeCheckoutConfigs = useStripeCheckoutConfigs();
  const productMetadata = useProductMetadata(appId);

  const sources = {
    policies: { state: readinessSourceState(policies), data: policies.data },
    paymentMethodOptions: {
      state: readinessSourceState(paymentMethodOptions),
      data: paymentMethodOptions.data,
    },
    paymentMethods: { state: readinessSourceState(paymentMethods), data: paymentMethods.data },
    stripeCheckoutConfigs: {
      state: readinessSourceState(stripeCheckoutConfigs),
      data: stripeCheckoutConfigs.data,
    },
    productMetadata: { state: readinessSourceState(productMetadata), data: productMetadata.data },
    // IAP / STRIPE 不读框架通道配置
    paymentProviderConfigs: { state: 'not_requested' as const, data: [] },
  };

  return (
    <Section
      title="通道下发阻断项"
      sub="只列出「什么在阻止该通道被 C 端下发」；不给出「会被下发」的结论 —— 判定链上还有若干项本页核不到，逐通道列在各区块末尾。"
    >
      {READINESS_CHANNEL_ORDER.map((paymentMethod) =>
        isFrameworkSettlementChannel(paymentMethod) ? (
          <FrameworkChannelReadinessBlock
            key={paymentMethod}
            appId={appId}
            channel={paymentMethod}
            input={{ appId, appIsActive: isActive, isNativeApp, ...sources }}
          />
        ) : (
          <ChannelReadinessBlock
            key={paymentMethod}
            appId={appId}
            readiness={buildPaymentChannelReadiness({
              appId,
              appIsActive: isActive,
              isNativeApp,
              paymentMethod,
              ...sources,
            })}
          />
        ),
      )}
    </Section>
  );
}

const NO_PROVIDER_CONFIGS: PaymentProviderConfigResponse[] = [];

/**
 * 框架通道区块：每个框架通道单独读自己的配置列表（每个区块一个 hook 实例），读不到即「无法判定」。
 */
function FrameworkChannelReadinessBlock({
  appId,
  channel,
  input,
}: {
  appId: string;
  channel: FrameworkSettlementChannel;
  input: Omit<Parameters<typeof buildPaymentChannelReadiness>[0], 'paymentMethod'>;
}) {
  const configs = usePaymentProviderConfigs(channel);
  const readiness = buildPaymentChannelReadiness({
    ...input,
    paymentMethod: channel,
    paymentProviderConfigs: {
      state: readinessSourceState(configs),
      data: configs.data ?? NO_PROVIDER_CONFIGS,
    },
  });
  return <ChannelReadinessBlock appId={appId} readiness={readiness} />;
}

/**
 * 单通道汇总 chip。
 *
 * **「未发现阻断项」刻意用中性 `chip`、不用 `chip-pos`** —— 绿色在本仓其余各处都读作「已就绪」，
 * 而本面板恰恰不作此断言。
 *
 * **阻断数、无法判定数与提示数同口径并列展示**，不能让其中一个吞掉另一个：adapter 侧有专门用例
 * 护住「阻断照列，undeterminable 不吞掉它」，chip 若二选一就在展示层把该取舍抹掉了。
 *
 * `noticeCount` 同样要出现在汇总层：把「运营的刻意配置」降成中性 notice 是为了不误报阻断，
 * **不是让它在汇总层消失**。「DE 覆盖组零成员（该地区全量支付中断）」这类只出 notice 的状态，
 * 汇总只写「未发现阻断项」等于把降档做成了静默丢弃。措辞刻意不暗示故障（中性 `chip`，且
 * 「未发现阻断项」照写）—— 它们多数确实是正常配置说明。
 * 阻断非零时不再叠加提示数：那一档运营该先处理阻断项，逐条文案就在下方。
 *
 * **「未启用」是一个前缀、不是一条早返分支**：`notApplicable` 只由策略组成员集合决定，与 ⓪ App
 * 启停正交（见 adapter `buildPaymentChannelReadiness`），故它**不蕴含零阻断**。早期版本在
 * `notApplicable` 上直接中性早返，于是一个**已停用** App 的未启用通道（STRIPE 不在策略组是默认态
 * —— `apps.is_stripe_enabled` 的 DB 默认值为 false）chip 与一个健康 App 的逐字、同色完全一致：
 * `blockerCount` 与 `chip-neg` 都到不了展示层。运营排查「Stripe 没上」时会照 chip 去策略组面板把
 * STRIPE 加进全局组（一次落 C 端 serving 表的写入 + audit log），刷新后依然不下发，此时才露出真正
 * 原因是 App 已停用。故这里改成并列渲染：既说「未启用」，也照常报 ⓪ 的阻断数与 `chip-neg`，
 * 与下方 ⓪ 行不再矛盾。阻断数用计数而非具体文案，是与本 chip 其余各段同一种表达。
 */
function ReadinessSummaryChip({ readiness }: { readiness: PaymentChannelReadiness }) {
  const parts: string[] = [];
  if (readiness.notApplicable) parts.push('未启用');
  if (readiness.blockerCount === 0 && readiness.undeterminableCount === 0)
    parts.push(
      readiness.notApplicable
        ? '本 App 策略组内无该通道'
        : `本页所核 ${readiness.checks.length} 项未发现阻断项`,
    );
  if (readiness.blockerCount > 0) parts.push(`${readiness.blockerCount} 项阻断`);
  if (readiness.undeterminableCount > 0) parts.push(`${readiness.undeterminableCount} 项无法判定`);
  // 未适用态自带的那条 policy notice 与「本 App 策略组内无该通道」说的是同一件事，不再叠加计数
  if (!readiness.notApplicable && readiness.blockerCount === 0 && readiness.noticeCount > 0)
    parts.push(`${readiness.noticeCount} 项提示`);
  const className =
    readiness.blockerCount > 0
      ? 'chip chip-neg'
      : readiness.undeterminableCount > 0
        ? 'chip chip-warn'
        : 'chip';
  return (
    <span className={className} style={{ fontSize: 10 }}>
      {parts.join(' · ')}
    </span>
  );
}

function ChannelReadinessBlock({
  appId,
  readiness,
}: {
  appId: string;
  readiness: PaymentChannelReadiness;
}) {
  const label = READINESS_CHANNEL_LABEL[readiness.paymentMethod];
  return (
    <div data-testid={`readiness-${readiness.paymentMethod}`} style={{ padding: '10px 0 2px' }}>
      <div className="hstack" style={{ gap: 10, padding: '4px 0 2px' }}>
        <span style={{ fontSize: 12.5, fontWeight: 500 }}>{label}</span>
        <ReadinessSummaryChip readiness={readiness} />
        <div style={{ flex: 1 }} />
        {readiness.paymentMethod === 'IAP' && !readiness.notApplicable && (
          <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
            IAP 无 Checkout 配置前置，本通道核 {readiness.checks.length} 项
          </span>
        )}
        {isFrameworkSettlementChannel(readiness.paymentMethod) && !readiness.notApplicable && (
          <span style={{ fontSize: 11, color: 'var(--fg-faint)' }}>
            本通道核 {readiness.checks.length} 项
          </span>
        )}
      </div>
      {readiness.checks.map((check) => (
        <ReadinessCheckRow
          key={check.id}
          appId={appId}
          channel={readiness.paymentMethod}
          check={check}
        />
      ))}
      {readiness.noBlockerFound && (
        <div style={{ padding: '6px 0 2px', fontSize: 11, color: 'var(--fg-faint)' }}>
          本页所核 {readiness.checks.length} 项均未发现阻断项 —— 这不等于该通道会被下发，另有{' '}
          {readiness.blindSpots.length} 项判据本页核不到（见下）。
        </div>
      )}
      <BlindSpotList readiness={readiness} />
    </div>
  );
}

/**
 * 本通道核不到的残余判据 —— **按通道分别列全**。
 *
 * 早期版本只列了回跳 host 一条、还写死「一项」，且 IAP 区块也指向那条 Stripe 专有说明。host 闸只对
 * STRIPE 生效（C 端 `payment-channel-candidates.ts` 里 `if (method.paymentMethod === 'STRIPE')`），
 * 与 IAP 完全无关；而实际残余判据两个通道条数不同（当前 STRIPE 6 条 / IAP 3 条，以
 * `READINESS_BLIND_SPOTS` 为准 —— 别在散文里写死条数，加一条就会漂）。
 */
function BlindSpotList({ readiness }: { readiness: PaymentChannelReadiness }) {
  const label = READINESS_CHANNEL_LABEL[readiness.paymentMethod];
  return (
    <div
      style={{
        margin: '8px 0 2px',
        padding: '8px 0 2px',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
        <Icons.IconAlert size={13} style={{ color: 'var(--warn)', flexShrink: 0, marginTop: 2 }} />
        <span style={{ fontSize: 11.5, color: 'var(--fg-muted)', fontWeight: 500 }}>
          {label} 还有 {readiness.blindSpots.length} 项判据本页核不到（以 C 端 serving 为准）
        </span>
      </div>
      <ul style={{ margin: '4px 0 0', paddingLeft: 30 }}>
        {readiness.blindSpots.map((note) => (
          <li
            key={note}
            style={{ fontSize: 11, color: 'var(--fg-faint)', lineHeight: 1.7, padding: '1px 0' }}
          >
            {note}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ReadinessCheckRow({
  appId,
  channel,
  check,
}: {
  appId: string;
  channel: SettlementChannel;
  check: ReadinessCheck;
}) {
  return (
    <FormRow label={check.title} hint={check.dimension}>
      {check.verdict === 'undeterminable' ? (
        <span style={{ fontSize: 11.5, color: 'var(--warn)' }}>
          无法判定 · {check.unavailableReason}
        </span>
      ) : (
        <div className="vstack" style={{ gap: 6 }}>
          {check.blockers.map((item) => (
            <ReadinessItemRow
              key={item.key}
              appId={appId}
              channel={channel}
              item={item}
              tone="blocker"
            />
          ))}
          {check.notices.map((item) => (
            <ReadinessItemRow
              key={item.key}
              appId={appId}
              channel={channel}
              item={item}
              tone="notice"
            />
          ))}
          {check.verdict === 'no_blocker_found' && check.notices.length === 0 && (
            <span style={{ fontSize: 11.5, color: 'var(--fg-faint)' }}>未发现阻断项</span>
          )}
        </div>
      )}
    </FormRow>
  );
}

function ReadinessItemRow({
  appId,
  channel,
  item,
  tone,
}: {
  appId: string;
  channel: SettlementChannel;
  item: ReadinessItem;
  tone: 'blocker' | 'notice';
}) {
  const isBlocker = tone === 'blocker';
  return (
    <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
      {isBlocker ? (
        <Icons.IconAlert size={12} style={{ color: 'var(--neg)', flexShrink: 0, marginTop: 2 }} />
      ) : (
        <Icons.IconInfo
          size={12}
          style={{ color: 'var(--fg-faint)', flexShrink: 0, marginTop: 2 }}
        />
      )}
      {item.scopeLabel && (
        <span className="chip" style={{ fontSize: 10, flexShrink: 0 }}>
          {item.scopeLabel}
        </span>
      )}
      <span
        style={{
          fontSize: 11.5,
          color: isBlocker ? 'var(--fg)' : 'var(--fg-muted)',
          lineHeight: 1.6,
        }}
      >
        {item.text}
      </span>
      <div style={{ flex: 1 }} />
      {item.action !== null && (
        <ReadinessActionControl appId={appId} channel={channel} target={item.action} />
      )}
    </div>
  );
}

/**
 * 处置入口：有路由目标且当前角色够 → `btn-sm` 按钮；否则退成「去哪儿 / 需谁处理」文案。
 *
 * 三类阻断分别落在三个页面（支付页面 / 支付产品 / Stripe 集成），面板不给入口的话运营得自己猜。
 * `/paywall` 跳转前先 `setAppId` —— 支付管理页按全局 app-scope 取数，不切就会落到别的 App。
 */
function ReadinessActionControl({
  appId,
  channel,
  target,
}: {
  appId: string;
  channel: SettlementChannel;
  target: ReadinessActionTarget;
}) {
  const navigate = useNavigate();
  const setAppId = useAppScope((state) => state.setAppId);
  const role = useAuthStore((state) => state.user?.role);
  const isRootAdmin = role === 'root' || role === 'admin';
  const action =
    target === 'provider_config' ? providerConfigAction(channel) : READINESS_ACTIONS[target];

  if (action.requiresRootAdmin && !isRootAdmin) {
    return (
      <span style={{ fontSize: 10.5, color: 'var(--fg-faint)', flexShrink: 0 }}>
        需 root / admin 在 {action.where} 处理
      </span>
    );
  }
  const to = action.to;
  if (to === null || action.buttonLabel === null) {
    return (
      <span style={{ fontSize: 10.5, color: 'var(--fg-faint)', flexShrink: 0 }}>
        在 {action.where} 处理
      </span>
    );
  }
  return (
    <button
      className="btn btn-sm"
      style={{ flexShrink: 0 }}
      onClick={() => {
        setAppId(appId);
        navigate(to);
      }}
    >
      <Icons.IconArrowRight size={11} />
      {action.buttonLabel}
    </button>
  );
}
